import React, { FC, useCallback, useContext, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { Button, Loader } from 'ncoded-component-library';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { getHasElementsInArray, safeParse, sentryError } from 'utils';
import { getIsEditorValueEmpty } from 'components/RichTextEditorField/utils';
import RichTextEditorField from 'components/RichTextEditorField';
import { getRichTextEditorEmptyValue } from 'components/RichTextEditorField/RichTextEditorField.component';
import { TagOption } from 'components/RichTextEditorField/components/TagOptions/TagOptions.component';
import ClipIcon from 'icons/Clip.icon';
import ResolvePopup from 'components/ResolvePopup';
import UploadingFiles from './components/UploadingFiles';
import { UploaderMessageFile } from 'hooks/useUploadMessageFile';
import { focusMessageFormInput, FocusTrigger, MESSAGE_FORM_ID } from './utils';
import ReplySuggestions from './components/ReplySuggestions';

import './MessageForm.styles.scss';

const MAX_NUM_OF_CHAR = 100000;

type MessageFormProps = {
  className?: string;
  focusTrigger?: FocusTrigger;
  disabled?: boolean;
  placeholder?: string;
  buttonText?: string;
  canTagPeople?: boolean;
  resetMessage?: string | number | boolean;
  uploadFileHintMessage?: string;
  tagOptions?: TagOption[];
  files?: UploaderMessageFile[];
  areAllFilesProcessed?: boolean;
  replySuggestions?: string[];
  loadingReplySuggestions?: boolean;
  onFileRemove?: (id: string) => void;
  onFileUpload?: React.MouseEventHandler<HTMLElement>;
  handleOnSubmit: (value: string) => Promise<void>;
  onReplySuggestionsRemove?: () => void;
  onReplySuggestionSelect?: (suggestion: string) => void;
};

const MessageForm: FC<MessageFormProps> = ({
  className,
  disabled,
  placeholder,
  buttonText,
  resetMessage,
  focusTrigger,
  canTagPeople,
  tagOptions,
  uploadFileHintMessage,
  files = [],
  areAllFilesProcessed = false,
  replySuggestions,
  loadingReplySuggestions,
  onFileRemove,
  onFileUpload,
  handleOnSubmit,
  onReplySuggestionsRemove,
  onReplySuggestionSelect,
}) => {
  const { popServerError } = useContext(PopNotificationsContext);

  const ref = useRef(null);
  const formElementRef = useRef<HTMLFormElement>(null);

  const hasFiles = getHasElementsInArray(files);

  const classes = classNames('message-form', { disabled }, className);

  const { t } = useTranslation();

  const resetForm = useCallback(() => {
    if (ref?.current) {
      ref.current.reset();
      ref.current.restart();
    }
  }, []);

  const focus = useCallback(
    () => focusMessageFormInput(formElementRef.current),
    [],
  );

  const handleReplySuggestionSelect = useCallback(
    (suggestion: string) => {
      ref.current?.change('message', getRichTextEditorEmptyValue(suggestion));
      onReplySuggestionSelect?.(suggestion);
      onReplySuggestionsRemove?.();
      focus();
    },
    [onReplySuggestionSelect, onReplySuggestionsRemove, focus],
  );

  const onSubmit = useCallback(
    async ({ message }) => {
      try {
        if (disabled || !message) return;

        await handleOnSubmit(message.trim());

        resetForm();
        focus();
      } catch (e) {
        sentryError(e);
        popServerError(e);
      }
    },
    [disabled, handleOnSubmit, resetForm, focus, popServerError],
  );

  useEffect(() => {
    resetForm();
  }, [resetMessage, resetForm]);

  useEffect(() => {
    if (focusTrigger) focus();
  }, [focus, focusTrigger]);

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{
        message: getRichTextEditorEmptyValue(),
      }}
      render={({
        pristine,
        form,
        hasValidationErrors,
        submitting,
        values: { message },
        handleSubmit,
      }) => {
        ref.current = form;

        return (
          <form
            ref={formElementRef}
            className={classes}
            onSubmit={handleSubmit}
          >
            {onReplySuggestionsRemove && (
              <ReplySuggestions
                message={message}
                suggestions={replySuggestions}
                loading={loadingReplySuggestions}
                onSelect={handleReplySuggestionSelect}
                onRemove={onReplySuggestionsRemove}
              />
            )}

            <div className="message-form__wrapper">
              <RichTextEditorField
                id={MESSAGE_FORM_ID}
                name="message"
                required
                showError={false}
                showMaxLength={false}
                readOnly={disabled}
                maxLength={MAX_NUM_OF_CHAR}
                {...(pristine && { placeholder })}
                {...(canTagPeople && { tagOptions })}
              />
              {hasFiles && (
                <UploadingFiles files={files} onRemove={onFileRemove} />
              )}
            </div>

            <div className="message-form__actions">
              {!disabled &&
                onFileUpload &&
                (uploadFileHintMessage ? (
                  <ResolvePopup onHover popupContent={uploadFileHintMessage}>
                    <Button
                      variant="icon"
                      disabled={disabled}
                      icon={ClipIcon}
                      onClick={onFileUpload}
                    />
                  </ResolvePopup>
                ) : (
                  <Button
                    variant="icon"
                    disabled={disabled}
                    icon={ClipIcon}
                    onClick={onFileUpload}
                  />
                ))}

              <Button
                type="submit"
                className="send-button"
                disabled={
                  disabled ||
                  hasValidationErrors ||
                  (getIsEditorValueEmpty(safeParse(message)) &&
                    !files.length) ||
                  (!!files.length && !areAllFilesProcessed)
                }
              >
                {submitting ? <Loader size="small" /> : buttonText || t('save')}
              </Button>
            </div>
          </form>
        );
      }}
    />
  );
};

export default MessageForm;
